import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Check, FileText, Search, Trash2 } from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import { Dialog } from '../../../../../../ui';
import {
  deleteJDTemplate,
  getJDTemplate,
  getJDTemplateList,
} from '../../../../../../utils/api_service/JobOpenings';
import {
  JDTemplate,
  JobDescription,
} from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { DIALOG_TYPE } from '../../../../Constants/DialogTypes';

interface LoadJdTemplateProps {
  closeModal: () => void;
  onTemplateSelect?: (template: JDTemplate) => void;
  jobDescription: JobDescription;
  setJobDescription: React.Dispatch<React.SetStateAction<JobDescription>>;
  setJobTitle: React.Dispatch<React.SetStateAction<SelectOptions | undefined>>;
}

export const LoadJdTemplate: React.FC<LoadJdTemplateProps> = ({
  closeModal,
  setJobDescription,
  onTemplateSelect,
  setJobTitle,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [templates, setTemplates] = useState<JDTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<JDTemplate>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 4;

  const getJobDescriptionTemplates = async () => {
    try {
      const response = await getJDTemplateList(
        searchTerm,
        currentPage,
        itemsPerPage,
      );
      if (response.ok) {
        const data = await response.json();
        setTotalCount(data.total_count);
        const formattedTemplates: JDTemplate[] = data.results.map(
          (item: any) => ({
            templateId: item.id,
            templateTitle: item.templateTitle,
            jobTitle: item.jobTitle.jobTitle ?? 'N/A',
            createdAt: item.createdAt,
            tags: item.tags ?? [],
            templateDescription: item.templateDescription ?? 'N/A',
          }),
        );

        setTemplates(formattedTemplates);
      } else {
        toast.error('Error loading templates', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Error loading templates', { position: 'bottom-center' });
    }
  };

  useEffect(() => {
    getJobDescriptionTemplates();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    const debounceSearchTerm = setTimeout(() => {
      getJobDescriptionTemplates();
    }, 200);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchTerm, currentPage]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleSelectTemplate = async () => {
    if (selectedTemplate) {
      try {
        const response = await getJDTemplate(selectedTemplate.templateId);
        if (response.ok) {
          const data = await response.json();
          data.jobOpeningId = id;

          setJobDescription((prev) => ({
            ...prev,
            ...data,
          }));

          setJobTitle({
            value: data.jobTitle.id,
            label: data.jobTitle.jobTitle,
          });

          if (onTemplateSelect) {
            onTemplateSelect(selectedTemplate);
          }

          toast.success('Template loaded successfully!', {
            position: 'bottom-center',
          });
          closeModal();
        } else {
          toast.error('Failed to load template details', {
            position: 'bottom-center',
          });
        }
      } catch (error) {
        toast.error('Error loading template details', {
          position: 'bottom-center',
        });
        closeModal();
      }
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await deleteJDTemplate(selectedTemplate.templateId);

      if (response.ok) {
        setSelectedTemplate(undefined);
        setIsModalOpen(false);

        await getJobDescriptionTemplates();

        toast.success('Template deleted successfully!', {
          position: 'bottom-center',
        });
      } else {
        toast.error('Failed to delete template', { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting template', { position: 'bottom-center' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: 1024, mx: 'auto', width: '100%' }}>
      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates by name, description, or tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#94a3b8" />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'background.paper' }}
        />
      </Box>

      {/* Results Info */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {totalCount} template{totalCount !== 1 ? 's' : ''} found
        </Typography>
        {selectedTemplate && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'primary.main' }}>
            <Check size={16} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedTemplate.templateTitle} selected
            </Typography>
          </Stack>
        )}
      </Stack>

      {/* Templates Grid */}
      <Box sx={{ flex: 1, minHeight: 0, mb: 3 }}>
        {templates.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            {templates.map((template) => {
              const isSelected = selectedTemplate?.templateId === template.templateId;
              return (
                <Paper
                  key={template.templateId}
                  variant="outlined"
                  onClick={() => setSelectedTemplate(template)}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'primary.50' : 'background.paper',
                    boxShadow: isSelected ? 2 : 1,
                    '&:hover': {
                      boxShadow: 3,
                      borderColor: 'primary.light',
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <Box sx={{ flex: 1, pr: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isSelected ? 'primary.dark' : 'text.primary' }}>
                        {template.templateTitle}
                      </Typography>
                      <Chip
                        label={template.jobTitle}
                        size="small"
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{ mt: 0.5, height: 22, fontSize: '11px', fontWeight: 600 }}
                      />
                    </Box>
                    {isSelected && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        <Check size={14} />
                      </Box>
                    )}
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {template.templateDescription}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {template.tags?.slice(0, 3).map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        sx={{ height: 20, fontSize: '11px' }}
                      />
                    ))}
                    {template.tags && template.tags.length > 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                        +{template.tags.length - 3} more
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Created at: {formatDate(template.createdAt)}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<Trash2 size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTemplate(template);
                        setIsModalOpen(true);
                      }}
                      sx={{ textTransform: 'none', py: 0.25, px: 1, fontSize: '11px' }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <FileText size={48} color="#94a3b8" />
            <Typography variant="h6" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
              No templates found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Try adjusting your search or filter criteria
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mb: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            size="small"
          />
        </Stack>
      )}

      {/* Action Buttons */}
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button variant="outlined" onClick={closeModal} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSelectTemplate}
          disabled={!selectedTemplate}
          sx={{ textTransform: 'none', px: 3 }}
        >
          Load Template
        </Button>
      </Stack>

      <Dialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Are you sure you want to delete this template?"
        dialogType={DIALOG_TYPE.ERROR}
        justify="center"
        confirmButtonLable="Delete Template"
        cancelButtonLable="Cancel"
        handleApply={handleDeleteTemplate}
      >
        <Typography variant="body2" color="text.secondary">
          This action cannot be undone. This template will be permanently removed from your library.
        </Typography>
      </Dialog>
    </Box>
  );
};
